import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatOption } from '@angular/material/core';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { CommonConstant } from '../../constants/common.constants';
import { IAutoCompleteField } from '../../interfaces/auto-complete-field.interface';
import { CustomValidatorUtils } from '../../utils/custom-validators.utils';
import { BaseComponent } from '../base-component/base.component';

@Component({
  selector: 'favie-auto-complete',
  templateUrl: './favie-auto-complete.component.html',
})
export class FavieAutoCompleteComponent extends BaseComponent implements OnChanges {
  @Input() field: IAutoCompleteField;
  @Input() control: FormControl;

  @Output() searchChanged = new EventEmitter<string>();
  @Output() optionSelected = new EventEmitter<MatAutocompleteSelectedEvent>();
  @Output() onClose = new EventEmitter<any>();
  @Output() onBlurChanged = new EventEmitter<any>();

  public scrollViewPortHeight: string;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.field?.currentValue) {
      this.updateScrollViewPortHeight()
    }

    if (changes.control?.currentValue) {
      this.updateValidators(this.control);
      this.subscribe(
        this.control.valueChanges.pipe(
          debounceTime(this.field?.debounceTime || CommonConstant.INPUT_DEBOUNCE_INTERVAL),
          distinctUntilChanged(),
        ),
        (searchValue) => {
          if (
            typeof searchValue === 'string' &&
            this.searchValueValidate(searchValue)
          ) {
            this.searchChanged.next(searchValue);
            this.updateValidators(this.control);

            const selectedOption = this.field.options?.find(
              (item) => item.label === searchValue
            );
            if (this.control.valid && selectedOption) {
              // update control value if the text that user input match the one in the dropdown
              this.control.setValue(selectedOption);
              this.optionSelected.next({
                source: null,
                option: { value: selectedOption } as MatOption,
              });
            }
          } else {
            if (this.field.initOptions) {
              this.field.options = this.field.initOptions;
            }
          }

          this.updateScrollViewPortHeight()
        }
      );
    }
  }

  selectOption(event: MatAutocompleteSelectedEvent) {
    this.optionSelected.next(event);
  }

  closeDropdown() {
    this.onClose.emit();
  }

  onBlur(event: any) {
    this.onBlurChanged.next(this.field.options);
  }

  displayFn(option: any): string {
    return option && option.label ? option.label : '';
  }

  private searchValueValidate(searchValue: string) {
    if (searchValue.length > 0) {
      return (
        (searchValue || '').trim().length >=
        (this.field.minLength >= 0
          ? this.field.minLength
          : CommonConstant.INPUT_DEBOUNCE_THRESH_HOLD)
      );
    }
    return true; // when clear search
  }

  private updateValidators(control: FormControl) {
    const validatorsList = [
      CustomValidatorUtils.valueMatchWithAutoCompleteField(this.field?.options),
    ];
    const currentValidator = control.validator && control.validator({} as AbstractControl);
    if (currentValidator && currentValidator.required) {
      validatorsList.push(Validators.required);
    }
    control.setValidators(validatorsList);
    control.updateValueAndValidity();
  }

  private updateScrollViewPortHeight() {
    // Recompute how big the viewport should be.
    if (this.field.options?.length < 4) {
      this.scrollViewPortHeight = this.field.options.length * 50 + 'px';
    } else {
      this.scrollViewPortHeight = '200px';
    }
  }
}
